<template>
  <div>
    <div class="stock-shell">
      <header class="stock-header">
        <div class="stock-header__title">
          <i class="bi bi-box-seam-fill"></i>
          <div>
            <h1>{{ auth.storeName || name_business }}</h1>
            <span class="stock-header__subtitle">Entrada e descarte de estoque</span>
          </div>
        </div>
        <div class="d-flex gap-2">
          <router-link v-if="auth.canMakeSale" :to="{ name: 'pdv' }" class="btn btn-outline-light btn-sm" title="Voltar para o PDV">
            <i class="bi bi-shop me-1"></i>Voltar ao PDV
          </router-link>
          <button type="button" @click="logout" class="btn btn-outline-light btn-sm">
            <i class="bi bi-box-arrow-right me-1"></i>Sair
          </button>
        </div>
      </header>

      <div class="stock-search">
        <div class="input-icon-group flex-grow-1">
          <i class="bi bi-search"></i>
          <input autocomplete="off" type="text" v-model="busca" class="form-control" placeholder="Buscar produto por nome...">
        </div>
      </div>

      <p v-if="!auth.canManageProduct" class="stock-no-permission">
        <i class="bi bi-exclamation-triangle-fill me-1"></i>
        Este usuário não tem permissão para movimentar estoque.
      </p>

      <div class="stock-product-grid">
        <div class="product-tile" v-for="produto in produtosFiltrados" :key="produto.id" @click="abrirMovimentacao(produto)">
          <div class="product-tile__name">{{ produto.name }}</div>
          <div class="product-tile__meta">
            <span class="product-tile__code"><i class="bi bi-upc"></i> {{ produto.code || '-' }}</span>
            <span class="product-tile__stock">{{ produto.stock }} {{ produto.measure }}</span>
          </div>
        </div>
        <p v-if="!produtosFiltrados.length" class="text-muted text-center w-100 py-4">Nenhum produto encontrado.</p>
      </div>
    </div>

    <!-- Modal Movimentação -->
    <div class="modal fade" id="modal_movimentacao" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ produtoSelecionado?.name }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">Estoque atual: {{ produtoSelecionado?.stock }} {{ produtoSelecionado?.measure }}</p>

            <div class="btn-group w-100 mb-3" role="group">
              <button type="button" class="btn" :class="modo === 'repor' ? 'btn-success' : 'btn-outline-success'" @click="modo = 'repor'">
                <i class="bi bi-plus-circle-fill me-1"></i>Repor
              </button>
              <button type="button" class="btn" :class="modo === 'descartar' ? 'btn-warning' : 'btn-outline-warning'" @click="modo = 'descartar'">
                <i class="bi bi-file-earmark-x-fill me-1"></i>Descartar
              </button>
            </div>

            <form @submit.prevent="confirmarMovimentacao">
              <div class="mb-3">
                <label class="form-label">Quantidade:</label>
                <input v-model="form.quant" type="number" step="0.01" min="0.01" required class="form-control form-control-lg">
              </div>
              <div class="mb-3" v-if="modo === 'repor'">
                <label class="form-label">Valor de compra (unitário):</label>
                <input v-model="form.buy" type="number" step="0.01" min="0" required class="form-control">
              </div>
              <div class="mb-3" v-else>
                <label class="form-label">Motivo do descarte:</label>
                <input v-model="form.description" type="text" class="form-control" placeholder="ex.: vencido, quebrado...">
              </div>
              <button type="submit" class="btn btn-primary w-100" :disabled="!auth.canManageProduct || salvando">
                {{ salvando ? 'Salvando...' : 'Confirmar' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Feedback -->
    <div class="modal fade" id="modal_feedback" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Sucesso</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body"><p>{{ msn_feedback }}</p></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { Modal } from "bootstrap";
import { useAuth } from '@/services/auth.js';
import { infos } from '@/services/infos';
import { products } from '@/models/products';

export default {
  name: 'StockView',
  setup() {
    const auth = useAuth();
    const Infos = infos();
    const Products = products();
    const name_business = Infos.business;

    const lista_produtos = ref([]);
    const busca = ref('');
    const modo = ref('repor');
    const salvando = ref(false);
    const msn_feedback = ref('');
    const produtoSelecionado = ref(null);

    const form = reactive({ quant: '', buy: '', description: '' });

    let modals = {};

    function openModal(id) {
      modals[id] = modals[id] ?? new Modal(document.getElementById(id));
      modals[id].show();
    }

    function closeModal(id) {
      modals[id]?.hide();
    }

    const produtosFiltrados = computed(() => {
      if (!busca.value) return lista_produtos.value;
      const termo = busca.value.toLowerCase();
      return lista_produtos.value.filter(p => p.name.toLowerCase().includes(termo));
    });

    async function carregarProdutos() {
      lista_produtos.value = await Products.list() ?? [];
    }

    function abrirMovimentacao(produto) {
      if (!auth.canManageProduct) return;
      produtoSelecionado.value = produto;
      modo.value = 'repor';
      form.quant = '';
      form.buy = '';
      form.description = '';
      openModal('modal_movimentacao');
    }

    async function confirmarMovimentacao() {
      salvando.value = true;
      const id = produtoSelecionado.value.id;
      const ok = modo.value === 'repor'
        ? await Products.restock(id, { quant: form.quant, buy: form.buy })
        : await Products.discard(id, { quant: form.quant, description: form.description });

      salvando.value = false;
      if (ok) {
        closeModal('modal_movimentacao');
        msn_feedback.value = modo.value === 'repor' ? 'Reposição registrada com sucesso!' : 'Descarte registrado com sucesso!';
        openModal('modal_feedback');
        carregarProdutos();
      }
    }

    function logout() {
      auth.logout();
    }

    onMounted(() => {
      carregarProdutos();
    });

    return {
      auth, name_business, busca, produtosFiltrados, produtoSelecionado, modo, form, salvando, msn_feedback,
      abrirMovimentacao, confirmarMovimentacao, logout,
    };
  },
}
</script>

<style scoped>
.stock-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  max-height: 100vh;
  background: var(--panel-bg);
}

.stock-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1.1rem;
  background: linear-gradient(135deg, var(--header-bg) 0%, var(--header-bg-soft) 100%);
  color: #fff;
  flex-shrink: 0;
}

.stock-header__title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
}

.stock-header__title i {
  font-size: 1.5rem;
  opacity: 0.85;
}

.stock-header__title h1 {
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0;
}

.stock-header__subtitle {
  font-size: 0.78rem;
  opacity: 0.75;
}

.stock-search {
  display: flex;
  gap: 0.6rem;
  padding: 0.85rem 1.1rem 0.5rem;
  flex-shrink: 0;
}

.stock-no-permission {
  margin: 0 1.1rem;
  padding: 0.6rem 0.9rem;
  border-radius: var(--radius-sm);
  background: #fff3cd;
  color: #664d03;
  font-size: 0.85rem;
}

.stock-product-grid {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 1.1rem 1.1rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 0.65rem;
  align-content: start;
}

.product-tile {
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  padding: 0.9rem 1rem;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
  background: var(--panel-bg);
}

.product-tile:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--brand);
  transform: translateY(-2px);
}

.product-tile__name {
  font-weight: 600;
  color: var(--text-strong);
  margin-bottom: 0.6rem;
  line-height: 1.25;
  min-height: 2.4em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-tile__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.product-tile__code {
  font-size: 0.75rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-tile__stock {
  font-weight: 700;
  color: var(--brand);
  white-space: nowrap;
}

.input-icon-group {
  position: relative;
}

.input-icon-group i {
  position: absolute;
  top: 50%;
  left: 0.9rem;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.input-icon-group .form-control {
  padding-left: 2.5rem;
  border-radius: var(--radius-sm);
}
</style>
